import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import React, { forwardRef, useMemo } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import {
  Fee,
  FeeType,
  Maybe,
  PaymentDetailFragment,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { StorageImage, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { usePrivateAction } from '@/lib/privateAction'

import { ApplyPreSaleFirstComeFirstServedValue } from '../FirstComeFirstServedDetailForm'

type PaymentInfoCellProps = {
  value: PaymentMethod
  setValue: UseFormSetValue<ApplyPreSaleFirstComeFirstServedValue>
  paymentSource: Maybe<PaymentDetailFragment> | undefined
  originFullNameValue: string
  error: string | undefined
  paymentFees?: Maybe<Fee[]>
  inputFullNameProps: Omit<TextInputFieldProps, 't' | 'type'>
  onRegister: () => void
  paymentMethods: PaymentMethod[]
  toggleMembershipModalOpen: () => void
  isAccessible: boolean
}
export const PaymentInfoCell: Cell<PaymentInfoCellProps> = ({
  inputFullNameProps,
  originFullNameValue,
  paymentSource,
  paymentFees,
  value,
  setValue,
  error,
  onRegister,
  paymentMethods,
  toggleMembershipModalOpen,
  isAccessible,
}) => {
  const { t } = useTranslation('firstComeFirstServedDetail')
  const { handleLoginModal } = usePrivateAction()

  const konbiniFee = useMemo(() => {
    return paymentFees?.find(fee => fee.feeType === FeeType.Konbini)
  }, [paymentFees])

  return (
    <CellFrame sx={{ pb: 4 }}>
      <Typography
        variant="h6"
        sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}
        color="text.secondary"
      >
        {t('paymentInfo', '支払い情報')}
      </Typography>
      <Stack
        sx={{
          px: 5,
          py: 3,
          '.MuiFormControlLabel-root > span:first-child': {
            display: paymentMethods.length > 1 ? 'block' : 'none',
          },
        }}
      >
        <FormControl fullWidth>
          <RadioGroup
            value={value}
            onChange={(_, value) => {
              setValue('paymentMethod', value as PaymentMethod, {
                shouldValidate: true,
              })
              if (value === PaymentMethod.Konbini) {
                setValue('fullName', originFullNameValue, {
                  shouldValidate: true,
                })
              }
            }}
            sx={{ gap: theme => theme.spacing(4) }}
          >
            {paymentMethods.includes(PaymentMethod.CreditCard) && (
              <FormControlLabel
                value={PaymentMethod.CreditCard}
                checked={value === PaymentMethod.CreditCard}
                control={
                  <Radio
                    onClick={e => {
                      if (!isAccessible) {
                        e.preventDefault()
                        toggleMembershipModalOpen()
                      }
                    }}
                  />
                }
                label={
                  paymentSource ? (
                    <Stack>
                      <Stack direction="row" spacing={3} alignItems="center">
                        <StorageImage
                          height={25}
                          src={generateCreditCardLogoPath(paymentSource.brand)}
                          target="common"
                        />
                        <Typography variant="h6">
                          {t('last4Digits', { number: paymentSource.last4 })}
                        </Typography>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="h6">{t('creditCard')}</Typography>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={handleLoginModal(onRegister)}
                        >
                          {t('register', '登録')}
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.hint">
                        {t(
                          'creditCardNotRegistered',
                          '支払い情報が登録されていません'
                        )}
                      </Typography>
                    </Stack>
                  )
                }
              />
            )}
            {paymentMethods.length > 1 && <Divider />}
            {paymentMethods.includes(PaymentMethod.Konbini) && (
              <FormControlLabel
                value={PaymentMethod.Konbini}
                checked={value === PaymentMethod.Konbini}
                control={
                  <Radio
                    onClick={e => {
                      if (!isAccessible) {
                        e.preventDefault()
                        toggleMembershipModalOpen()
                      }
                    }}
                  />
                }
                label={
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h5">
                        {t('convenienceStore', 'コンビニ決済')}
                      </Typography>
                      {!!konbiniFee?.formattedFee && (
                        <Typography variant="body2">
                          {t('convenienceStoreNote', {
                            paymentFee: konbiniFee.formattedFee,
                          })}
                        </Typography>
                      )}
                    </Stack>
                    <Typography variant="caption">
                      {t('convenienceStoreDesc')}
                    </Typography>
                  </Stack>
                }
              />
            )}
          </RadioGroup>
        </FormControl>
        {value === PaymentMethod.Konbini && (
          <>
            <Typography variant="caption" sx={{ py: 4 }} color="text.secondary">
              {t('storeItemNote')}
            </Typography>
            <TextInputCell
              type="text"
              {...inputFullNameProps}
              textFieldProps={{ sx: { mb: 0 } }}
            />
          </>
        )}
      </Stack>
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    </CellFrame>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('lotteryDetail')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)
